import client from '@shared/utils/client-rest'
import { query } from '@shared/utils/fetch-cache'
import * as v from 'valibot'
import type {
	getOptimusPhaseMappingSchema,
	getOptimusSequenceSchema,
	getTmsAtomicMovementSchema,
	tms_controller_settings_data,
	tms_coordination_settings_data,
	tms_phase_settings_data,
	tms_sequence_data,
} from '../../../shared-schemas/optimus/optimus-config-v2'
import {
	getNetworkOptimusConfig,
	type getOptimusControllerSettingsSchema,
	type getOptimusCoordinationSettingsSchema,
	type getOptimusPhaseSettingsSchema,
	type getOptimusTimeOfDaySchema,
	type getOptimusTodGroupSchema,
} from '../../../shared-schemas/optimus/optimus-config-v2'

export type INetworkOptimusConfiguration = v.InferOutput<ReturnType<typeof getNetworkOptimusConfig>>

export type ITodGroup = v.InferOutput<ReturnType<typeof getOptimusTodGroupSchema>>
export type ITimeOfDayRow = v.InferOutput<ReturnType<typeof getOptimusTimeOfDaySchema>>

export type ICoordinationSettings = v.InferOutput<ReturnType<typeof getOptimusCoordinationSettingsSchema>>
export type ICoordinationSettingsData = v.InferOutput<typeof tms_coordination_settings_data>

export type IPhaseSettings = v.InferOutput<ReturnType<typeof getOptimusPhaseSettingsSchema>>
export type IPhaseSettingsData = v.InferOutput<typeof tms_phase_settings_data>

export type IControllerSettings = v.InferOutput<ReturnType<typeof getOptimusControllerSettingsSchema>>
export type IControllerSettingsData = v.InferOutput<typeof tms_controller_settings_data>

export type ISequenceSettings = v.InferOutput<ReturnType<typeof getOptimusSequenceSchema>>
export type ISequenceSettingsData = v.InferOutput<typeof tms_sequence_data>

export type IPhaseMappingSettings = v.InferOutput<ReturnType<typeof getOptimusPhaseMappingSchema>>
export type IPhaseMappingSettingsData = IPhaseMappingSettings['phase_mapping']
export type IPhaseMappingAtomicMovement = v.InferOutput<ReturnType<typeof getTmsAtomicMovementSchema>>

export const OptimusConfigurationCacheKeys = {
	network_optimus_configuration: (customer_id: string, network_id: string) =>
		`optimus-configuration:network-optimus-configuration:${customer_id}:${network_id}`,
}

// Promise-returning fetch of the assembled network config (parsed to the short-id shape).
export function fetchNetworkOptimusConfigOnce(
	customer_id: string,
	network_id: string,
): Promise<INetworkOptimusConfiguration> {
	return client('/api/v1/pages/optimus/configuration', {
		params: {
			customer_id,
			network_id,
		},
	}).then((data) => v.parse(getNetworkOptimusConfig({ expected_format: 'short' }), data))
}

export function fetchNetworkOptimusConfig(customer_id: string, network_id: string) {
	return query(
		OptimusConfigurationCacheKeys.network_optimus_configuration(customer_id, network_id),
		() => fetchNetworkOptimusConfigOnce(customer_id, network_id),
	)
}

// PUT the assembled network config. The backend validates and returns the same shape, so we
// validate the body with the same schema before sending and parse the response back into the
// short-id form.
export function saveNetworkOptimusConfig(
	customer_id: string,
	network_id: string,
	config: INetworkOptimusConfiguration,
): Promise<INetworkOptimusConfiguration> {
	const body = v.parse(getNetworkOptimusConfig({ expected_format: 'short' }), config)
	return client('/api/v1/pages/optimus/configuration', {
		method: 'PUT',
		params: {
			customer_id,
			network_id,
		},
		body,
	}).then((data) => v.parse(getNetworkOptimusConfig({ expected_format: 'short' }), data))
}
